import type { AdminSponsorshipPage, AdminSponsorshipResponse } from '@inflip/contracts/admin';
import {
  SOCIAL_PLATFORM_LABELS,
  SPONSORSHIP_APPROVAL_MODE_LABELS,
  SPONSORSHIP_ELIGIBILITY_PLATFORMS,
  SPONSORSHIP_REGION_LABELS,
  SponsorshipStatus,
} from '@inflip/shared';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  Button,
  Chip,
  ConfirmDialog,
  EmptyState,
  ErrorBanner,
  InfoRow,
  PageHeader,
  PromptDialog,
  inputCls,
} from '../components/ui';
import { adminAssetUrl, api } from '../lib/api-client';
import { useAdminAuth } from '../lib/auth-context';
import { SPONSORSHIP_STATUS_META } from '../lib/status-ui';
import { formatDateTime } from '../lib/time-zone';
import { useListViewState } from '../lib/use-list-view-state';
import { notifyPendingWorkChanged } from '../lib/use-pending-work';
import { useSeenVersions } from '../lib/use-seen-versions';

const PAGE_SIZE = 20;

type DialogState =
  | null
  | { kind: 'approve'; sponsorship: AdminSponsorshipResponse }
  | { kind: 'reject'; sponsorship: AdminSponsorshipResponse };

/** 협찬 심사 — 회원이 등록(수정)한 협찬을 확인하고 공개하거나 반려한다. 공개 중인 협찬을 내릴 수도 있다 */
export function SponsorshipsReviewPage() {
  const { session } = useAdminAuth();
  const [filter, setFilter] = useListViewState<SponsorshipStatus | ''>(
    'status',
    SponsorshipStatus.PENDING,
  );
  const [page, setPage] = useListViewState('page', 1);
  // 검색어 — 협찬 지원 내역에서 협찬 제목을 누르면 그 제목이 채워진 채로 들어온다
  const [searchTerm, setSearchTerm] = useListViewState('q', '');
  const [q, setQ] = useState(searchTerm);
  const [result, setResult] = useState<AdminSponsorshipPage | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [dialog, setDialog] = useState<DialogState>(null);
  const token = session?.accessToken;
  const seen = useSeenVersions();
  const loadedQuery = useRef<string | null>(null);

  const load = useCallback(() => {
    if (!token) return Promise.resolve();
    // 관리자가 직접 부른 목록(처음·필터·쪽·검색)만 "본 것" — 토큰 갱신으로 다시 불러온 건 아니다
    const queryKey = `${filter}|${page}|${searchTerm}`;
    const baseline = loadedQuery.current !== queryKey;
    loadedQuery.current = queryKey;
    return api
      .listSponsorships(token, {
        page,
        pageSize: PAGE_SIZE,
        ...(filter ? { status: filter } : {}),
        ...(searchTerm ? { q: searchTerm } : {}),
      })
      .then((res) => {
        seen.observe(
          res.items.map((x) => [x.id, x.updatedAt]),
          baseline,
        );
        setResult(res);
        // 검색 결과가 하나뿐이면 바로 펼쳐 내용을 보여 준다
        if (searchTerm && res.items.length === 1) setOpenId(res.items[0]!.id);
      })
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, filter, page, searchTerm]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(fn: () => Promise<unknown>) {
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      await fn();
      setDialog(null);
      await load();
      // 사이드바 빨간 점도 바로 다시 센다
      notifyPendingWorkChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      // 그사이 다른 어드민·회원이 상태를 바꿔 거절됐을 수 있다 — 지금 상태로 다시 그린다
      setDialog(null);
      await load();
    } finally {
      setPending(false);
    }
  }

  if (!session || !token) return null;
  const rows = result?.items ?? [];

  return (
    <main className="w-full px-6 py-8">
      <PageHeader
        title="협찬 심사"
        description={
          result
            ? `${filter ? SPONSORSHIP_STATUS_META[filter].label : '전체'} ${result.total.toLocaleString('ko-KR')}건`
            : '회원이 등록한 협찬을 확인하고 공개하거나 반려해요'
        }
        actions={
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setPage(1);
              setSearchTerm(q.trim());
            }}
          >
            <input
              className={`w-72 ${inputCls}`}
              placeholder="협찬 제목·업체명 검색"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <Button type="submit">검색</Button>
          </form>
        }
      />

      <div className="mb-4 flex flex-wrap gap-1.5">
        <Chip
          active={filter === ''}
          onClick={() => {
            setPage(1);
            setFilter('');
          }}
        >
          전체
        </Chip>
        {Object.entries(SPONSORSHIP_STATUS_META).map(([k, v]) => (
          <Chip
            key={k}
            active={filter === k}
            onClick={() => {
              setPage(1);
              setFilter(k as SponsorshipStatus);
            }}
          >
            {v.label}
          </Chip>
        ))}
      </div>

      {error && <ErrorBanner message={error} />}

      {result && rows.length === 0 ? (
        <EmptyState message="해당 상태의 협찬이 없어요." />
      ) : (
        <div className="divide-y divide-border/60">
          {rows.map((s) => {
            const open = openId === s.id;
            // 화면을 연 뒤 회원이 고쳤거나 새로 들어온 심사 — 확인 전에는 승인·반려를 내지 않는다
            const stale = s.status === SponsorshipStatus.PENDING && seen.isStale(s.id, s.updatedAt);
            // 승인됐던 협찬을 고쳐 다시 들어온 심사 — 승인된 내용과 바뀐 항목을 표시한다
            const before = s.status === SponsorshipStatus.PENDING ? s.approved : null;
            const edited =
              before !== null &&
              (before.title !== s.title ||
                before.businessName !== s.businessName ||
                before.description !== s.description ||
                before.benefit !== s.benefit ||
                before.categoryId !== s.categoryId ||
                before.region !== s.region ||
                before.location !== s.location ||
                before.approvalMode !== s.approvalMode ||
                (before.visitRequired !== undefined && before.visitRequired !== s.visitRequired) ||
                conditionText(before.requirements) !== conditionText(s.requirements));
            return (
              <div key={s.id}>
                <button
                  type="button"
                  onClick={() => setOpenId(open ? null : s.id)}
                  className="-mx-2 flex w-[calc(100%+1rem)] items-center gap-3 rounded-md px-2 py-3 text-left transition hover:bg-muted"
                >
                  {s.imagePath ? (
                    <img
                      src={adminAssetUrl(s.imagePath)}
                      alt=""
                      className="h-12 w-12 shrink-0 rounded-md bg-muted object-cover"
                    />
                  ) : (
                    <span className="grid h-12 w-12 shrink-0 place-items-center rounded-md bg-muted text-xs text-muted-foreground">
                      기본
                    </span>
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="flex min-w-0 items-center gap-1.5">
                      <span className="truncate text-sm font-semibold">{s.title}</span>
                      {stale && (
                        <span className="shrink-0 whitespace-nowrap rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-bold text-rose-700">
                          {seen.isNew(s.id) ? '새로 들어옴' : '내용 바뀜'}
                        </span>
                      )}
                      {before && (
                        <span className="shrink-0 whitespace-nowrap rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-bold text-blue-700">
                          {edited ? '승인 후 수정' : '승인 후 재심사'}
                        </span>
                      )}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {[
                        s.businessName,
                        s.categoryLabel ?? '(없어진 종목)',
                        SPONSORSHIP_REGION_LABELS[s.region],
                      ].join(' · ')}{' '}
                      — {s.ownerName} ({s.ownerEmail})
                    </span>
                  </span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {formatDateTime(s.updatedAt)}
                  </span>
                  <span
                    className={`w-16 shrink-0 text-right text-xs font-bold ${SPONSORSHIP_STATUS_META[s.status].cls}`}
                  >
                    {SPONSORSHIP_STATUS_META[s.status].label}
                  </span>
                </button>

                {open && (
                  <div className="mb-4 rounded-lg bg-muted/50 px-4 py-3">
                    {before && (
                      <p className="pb-2 text-xs text-muted-foreground">
                        {edited ? (
                          <>
                            승인됐던 협찬을 고쳐 다시 들어온 심사예요. 바뀐 항목은 승인된 내용에{' '}
                            <span className="line-through">줄</span>을 긋고{' '}
                            <span className="font-semibold text-blue-600">바뀐 내용</span>을
                            파란색으로 보여요. 이미지는 심사 대상이 아니라 비교하지 않아요.
                          </>
                        ) : (
                          '승인됐던 협찬을 다시 저장해 들어온 심사예요. 승인된 내용과 달라진 항목은 없어요.'
                        )}
                      </p>
                    )}
                    <div className="divide-y divide-border/60">
                      {/* 목록 줄에 보이는 제목·업체·종목·지역은 바뀌었을 때만 따로 보여 준다 */}
                      {before && before.title !== s.title && (
                        <InfoRow label="제목">
                          <Diff before={before.title} after={s.title} />
                        </InfoRow>
                      )}
                      {before && before.businessName !== s.businessName && (
                        <InfoRow label="업체명">
                          <Diff before={before.businessName} after={s.businessName} />
                        </InfoRow>
                      )}
                      {before && before.categoryId !== s.categoryId && (
                        <InfoRow label="종목">
                          <Diff
                            before={before.categoryLabel ?? '(없어진 종목)'}
                            after={s.categoryLabel ?? '(없어진 종목)'}
                          />
                        </InfoRow>
                      )}
                      {before && before.region !== s.region && (
                        <InfoRow label="지역">
                          <Diff
                            before={SPONSORSHIP_REGION_LABELS[before.region]}
                            after={SPONSORSHIP_REGION_LABELS[s.region]}
                          />
                        </InfoRow>
                      )}
                      <InfoRow label="제공 내용">
                        <Diff before={before?.benefit} after={s.benefit} />
                      </InfoRow>
                      <InfoRow label="소개와 요청 사항">
                        <Diff before={before?.description} after={s.description} />
                      </InfoRow>
                      <InfoRow label="지원 조건">
                        <Diff
                          before={before ? conditionText(before.requirements) : undefined}
                          after={conditionText(s.requirements)}
                        />
                      </InfoRow>
                      <InfoRow label="방문">
                        <Diff
                          before={
                            before?.visitRequired === undefined
                              ? undefined
                              : visitLabel(before.visitRequired)
                          }
                          after={visitLabel(s.visitRequired)}
                        />
                      </InfoRow>
                      <InfoRow label="승인 방식">
                        <Diff
                          before={
                            before
                              ? SPONSORSHIP_APPROVAL_MODE_LABELS[before.approvalMode]
                              : undefined
                          }
                          after={SPONSORSHIP_APPROVAL_MODE_LABELS[s.approvalMode]}
                        />
                      </InfoRow>
                      <InfoRow label="상세 위치">
                        <Diff
                          before={before ? (before.location ?? '—') : undefined}
                          after={s.location ?? '—'}
                        />
                      </InfoRow>
                      <InfoRow label="지원">{s.applicationCount.toLocaleString('ko-KR')}명</InfoRow>
                      {s.rejectReason && <InfoRow label="반려 사유">{s.rejectReason}</InfoRow>}
                    </div>
                    <div className="mt-3 flex justify-end gap-1.5">
                      {stale ? (
                        <>
                          <span className="self-center text-xs text-rose-700">
                            화면을 연 뒤 회원이 내용을 바꿨어요. 위 내용을 확인해 주세요.
                          </span>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => seen.acknowledge(s.id, s.updatedAt)}
                          >
                            확인했어요
                          </Button>
                        </>
                      ) : (
                        <>
                          {(s.status === SponsorshipStatus.PENDING ||
                            s.status === SponsorshipStatus.OPEN) && (
                            <Button
                              size="sm"
                              variant="danger"
                              disabled={pending}
                              onClick={() => setDialog({ kind: 'reject', sponsorship: s })}
                            >
                              {s.status === SponsorshipStatus.OPEN ? '공개 내리기' : '반려'}
                            </Button>
                          )}
                          {s.status === SponsorshipStatus.PENDING && (
                            <Button
                              size="sm"
                              disabled={pending}
                              onClick={() => setDialog({ kind: 'approve', sponsorship: s })}
                            >
                              승인하고 공개
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {result && result.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-3">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            이전
          </Button>
          <span className="text-sm tabular-nums text-muted-foreground">
            {result.page} / {result.totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= result.totalPages}
            onClick={() => setPage(page + 1)}
          >
            다음
          </Button>
        </div>
      )}

      {dialog?.kind === 'approve' && (
        <ConfirmDialog
          title="이 협찬을 공개할까요?"
          description={`"${dialog.sponsorship.title}" — 승인하면 협찬 목록에 바로 올라가요.`}
          confirmLabel="승인하고 공개"
          busy={pending}
          onConfirm={() =>
            void run(() =>
              api.approveSponsorship(token, dialog.sponsorship.id, {
                seenUpdatedAt: seen.seenOf(dialog.sponsorship.id, dialog.sponsorship.updatedAt),
              }),
            )
          }
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'reject' && (
        <PromptDialog
          title={
            dialog.sponsorship.status === SponsorshipStatus.OPEN
              ? '공개 중인 협찬을 내릴까요?'
              : '이 협찬을 반려할까요?'
          }
          description="사유는 등록한 회원에게 알림으로 그대로 전달돼요."
          placeholder="반려 사유"
          submitLabel={dialog.sponsorship.status === SponsorshipStatus.OPEN ? '내리기' : '반려'}
          danger
          busy={pending}
          onSubmit={(reason) =>
            void run(() =>
              // 화면이 본 상태로 어떤 처리인지 정해 보낸다 — 그사이 상태가 바뀌었으면 서버가 거절한다
              dialog.sponsorship.status === SponsorshipStatus.OPEN
                ? api.takeDownSponsorship(token, dialog.sponsorship.id, { reason })
                : api.rejectSponsorship(token, dialog.sponsorship.id, {
                    reason,
                    seenUpdatedAt: seen.seenOf(dialog.sponsorship.id, dialog.sponsorship.updatedAt),
                  }),
            )
          }
          onClose={() => setDialog(null)}
        />
      )}
    </main>
  );
}

/** 지원 조건 한 줄 — "스레드 10,000명 이상 또는 …", 없으면 "조건 없음" */
function conditionText(requirements: AdminSponsorshipResponse['requirements']): string {
  const conditions = SPONSORSHIP_ELIGIBILITY_PLATFORMS.flatMap((platform) => {
    const min = requirements[platform];
    return min ? [`${SOCIAL_PLATFORM_LABELS[platform]} ${min.toLocaleString('ko-KR')}명 이상`] : [];
  });
  return conditions.length > 0 ? conditions.join(' 또는 ') : '조건 없음';
}

/**
 * 승인된 내용과 지금 내용 — 같거나 비교할 승인본이 없으면 지금 내용만,
 * 다르면 승인된 내용에 줄을 긋고 아래에 바뀐 내용을 파란색으로.
 */
function Diff({ before, after }: { before?: string; after: string }) {
  if (before === undefined || before === after) {
    return <span className="whitespace-pre-line">{after}</span>;
  }
  return (
    <span className="flex flex-col items-end gap-1">
      <del className="whitespace-pre-line text-muted-foreground">{before}</del>
      <ins className="whitespace-pre-line font-semibold text-blue-600 no-underline">{after}</ins>
    </span>
  );
}

function visitLabel(visitRequired: boolean): string {
  return visitRequired ? '방문해서 진행' : '방문 없이 진행';
}
